<template>
  <!-- Same card as the Search page: image, title, price (+ struck original),
       heart button, seller line. The parent owns favourites state. -->
  <div class="product-card" @click="$emit('open')">
    <div class="product-image">
      <img :src="image" :alt="title" @error="handleImageError" loading="lazy">
    </div>
    <div class="product-details">
      <h3 class="product-text-title">{{ title }}</h3>
      <div class="product-price-row">
        <div v-if="originalPrice" class="price-container">
          <p class="product-price original-price">{{ originalPrice }}</p>
          <p class="product-price new-price">{{ price }}</p>
        </div>
        <p v-else class="product-price">{{ price }}</p>
        <button class="favourite-btn" @click.stop.prevent="$emit('toggle-favourite')">
          <i
            v-if="favourited"
            class="fas fa-heart favourite-heart is-active"
            :class="{ popping }"
          ></i>
          <i v-else class="far fa-heart favourite-heart"></i>
        </button>
      </div>
      <div class="seller-details" v-if="sellerName">
        <div class="seller-info">
          <img
            :src="sellerImage || defaultSellerImage"
            :alt="sellerName"
            @error="handleSellerImageError"
            class="seller-image"
            loading="lazy"
          >
          <span class="seller-name">{{ sellerName }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_SELLER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
const DEFAULT_PRODUCT_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';

export default {
  name: 'ProductCard',
  props: {
    image: { type: String, default: '' },
    title: { type: String, default: '' },
    price: { type: String, default: '' },
    originalPrice: { type: String, default: '' },
    sellerName: { type: String, default: '' },
    sellerImage: { type: String, default: '' },
    favourited: { type: Boolean, default: false },
    popping: { type: Boolean, default: false }
  },
  data() {
    return { defaultSellerImage: DEFAULT_SELLER_IMAGE };
  },
  methods: {
    handleImageError(event) {
      event.target.src = DEFAULT_PRODUCT_IMAGE;
    },
    handleSellerImageError(event) {
      event.target.src = DEFAULT_SELLER_IMAGE;
    }
  }
};
</script>

<style scoped>
/* Mirrors the card styles in Search.vue. */
.product-card {
  position: relative;
  overflow: hidden;
  background: white;
  cursor: pointer;
}

.product-image {
  position: relative;
  width: 100%;
  height: 18rem;
  background: #f8f8f8;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.product-details {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-text-title {
  margin: 0;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  height: 2.8em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.product-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-container {
  display: flex;
  align-items: center;
}

.product-price {
  font-size: 1.1rem;
  color: #e44d26;
  font-weight: bold;
  margin: 0;
}

.product-price.original-price {
  text-decoration: line-through;
  color: black;
  font-size: 0.9em;
  font-weight: normal;
  margin-right: 5px;
}

.product-price.new-price {
  color: red;
}

.favourite-btn {
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.favourite-heart {
  font-size: 18px;
}

.favourite-heart.is-active {
  color: #ff4444;
}

.favourite-heart.popping {
  animation: favPop 200ms ease-out;
}

@keyframes favPop {
  0% { transform: scale(0.85); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1.0); }
}

.seller-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.seller-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seller-image {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.seller-name {
  color: #666;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

@media (max-width: 760px) {
  .product-image {
    height: 14rem;
  }

  .product-details {
    padding: 8px;
  }

  .product-text-title {
    font-size: 0.85rem;
  }
}

@media (max-width: 470px) {
  .product-image {
    height: 11rem;
  }

  .product-price {
    font-size: 1rem;
  }

  .seller-image {
    width: 20px;
    height: 20px;
  }

  .seller-name {
    font-size: 0.8rem;
  }
}
</style>
